// WhatsApp settings, read-only, from wa_meta/config (written by `wa publish`).
import { Link as LinkIcon, Table } from "@phosphor-icons/react";
import { Ic, useTick } from "@/components/bits";
import { EVENT } from "@/lib/fields";
import { ago, fmt } from "@/lib/format";
import { varsFor } from "@/model/templates";
import { useApp } from "@/shell/appData";

function source(src: string): string {
  if (src.startsWith("config:")) return `Event config · ${src.slice(7)}`;
  if (src.startsWith("const:")) return `Fixed text · ${src.slice(6)}`;
  return src;
}

export function Settings() {
  const { config, people, counts } = useApp();
  const now = useTick(60_000);
  const sample = people.find((p) => p.regs.some((r) => r.inSheet))?.regs[0];
  const ex = sample ? varsFor(sample, config) : {};
  const yaml = `services/whatsapp/events/${EVENT.slug}.yaml`;

  if (!config) {
    return (
      <div className="page narrow">
        <div className="pghead">
          <div className="t">
            <h1>WhatsApp settings</h1>
            <p>How confirmations are built and sent for this event.</p>
          </div>
        </div>
        <div className="empty">
          <h3>No settings published yet</h3>
          <p>
            Run <span className="mono">wa publish --event {EVENT.slug} --source sheet</span> from services/whatsapp.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="page narrow">
      <div className="pghead">
        <div className="t">
          <h1>WhatsApp settings</h1>
          <p>
            How confirmations are built and sent for this event. Read-only: change <span className="mono">{yaml}</span>{" "}
            and run <span className="mono">wa publish</span>.
          </p>
        </div>
      </div>
      <div className="set">
        <div>
          <h3>Source</h3>
          <p>Where the list of people comes from. The admin sees it as of the last `wa publish`.</p>
        </div>
        <div className="card plain">
          <div className="frow">
            <span className="row" style={{ padding: 0, border: 0, minHeight: 0, width: "auto" }}>
              <span className="ic ok">
                <Ic i={Table} />
              </span>
            </span>
            <div className="l">
              <b data-testid="source-name">{config.source.name || "—"}</b>
              <span>
                {config.source.kind === "sheet" ? "Google Sheet" : config.source.kind || "Source"} ·{" "}
                {fmt(counts.registrations)} registrations · published {ago(config.publishedAt, now)}
              </span>
            </div>
            {config.source.sheetId ? (
              <a
                className="btn line xs"
                href={`https://docs.google.com/spreadsheets/d/${config.source.sheetId}`}
                target="_blank"
                rel="noreferrer"
              >
                <Ic i={LinkIcon} />
                Open
              </a>
            ) : null}
          </div>
        </div>
      </div>
      <div className="set">
        <div>
          <h3>Template</h3>
          <p>Which column fills each blank in the message.</p>
        </div>
        <div className="stack-gap" style={{ gap: 12 }}>
          <div className="card plain">
            <div className="frow">
              <div className="l">
                <b className="mono" style={{ fontSize: 12.5, wordBreak: "break-all" }}>
                  {config.template}
                </b>
                <span>English · approval is checked by the CLI before a live send</span>
              </div>
            </div>
          </div>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Blank</th>
                  <th>Filled from</th>
                  <th className="hide-sm">Example</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(config.params).map(([p, src]) => (
                  <tr key={p}>
                    <td>
                      <span className="mapv">{`{{${p}}}`}</span>
                    </td>
                    <td>{source(src)}</td>
                    <td className="sub hide-sm">{ex[p] ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="set">
        <div>
          <h3>Sending</h3>
          <p>Limits for this event.</p>
        </div>
        <div className="card plain">
          <div className="frow">
            <div className="l">
              <b>Daily cap</b>
              <span>Unique phones per rolling 24 hours (WhatsApp tier, minus headroom).</span>
            </div>
            <span className="num" style={{ fontWeight: 500 }} data-testid="daily-cap">
              {fmt(config.dailyCap)}
            </span>
          </div>
          <div className="frow">
            <div className="l">
              <b>Event</b>
              <span>Firestore key and CLI name</span>
            </div>
            <span className="mono muted">
              {EVENT.key} · {EVENT.slug}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
